import React, { useEffect, useMemo, useState, useCallback } from "react";
import Body from "../../layouts/Body";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Autocomplete,
  TextField,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CloseIcon from "@mui/icons-material/Close";
import GroupsIcon from "@mui/icons-material/Groups";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import MUIDataTable from "mui-datatables";
import { useUserContext } from "../../context/UserContext";
import {
  getActiveFscList,
  createFscFacilitatorMapping,
  deactivateFscFacilitatorMapping,
  getActiveCentersByModel,
  getFacilitatorsByCenter,
} from "../../utils/services";

const MentorMapping = () => {
  const { user } = useUserContext();
  const token = user?.token;

  // --- States ---
  const [modelList, setModelList] = useState([]);
  const [centerList, setCenterList] = useState([]);
  const [isSearched, setIsSearched] = useState(false);
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedCenter, setSelectedCenter] = useState("");
  const [facilitatorList, setFacilitatorList] = useState([]);
  const [fscList, setFscList] = useState([]);

  // Loaders
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [loadingFacilitators, setLoadingFacilitators] = useState(false);
  const [loadingFsc, setLoadingFsc] = useState(false);
  const [saving, setSaving] = useState(false);

  // Dialog State
  const [fscDialogOpen, setFscDialogOpen] = useState(false);
  const [selectedFacilitator, setSelectedFacilitator] = useState(null);
  const [selectedFsc, setSelectedFsc] = useState("");
  const [originalFsc, setOriginalFsc] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");

  const isFormUnchanged =
    String(selectedFsc) === String(originalFsc) &&
    String(selectedProgram) === String(selectedFacilitator?.programId || "");

  // --- API Handlers ---
  const fetchCenters = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingCenters(true);
      const result = await getActiveCentersByModel(token);
      const data = Array.isArray(result) ? result : result?.data || [];
      setCenterList(data);

      const uniqueModels = [];
      data.forEach((item) => {
        const modelId = item.newModelId;
        const modelName = item.longCode;

        if (
          modelId !== null &&
          modelId !== undefined &&
          !uniqueModels.some((model) => model.id === modelId)
        ) {
          uniqueModels.push({ id: modelId, name: modelName });
        }
      });

      setModelList(uniqueModels);
    } catch (error) {
      console.error("Error fetching centers:", error);
    } finally {
      setLoadingCenters(false);
    }
  }, [token]);

  const fetchFscList = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingFsc(true);
      const response = await getActiveFscList(token);
      const data = response?.data || [];
      setFscList(data);
    } catch (error) {
      console.error("Error fetching FSC list:", error);
      setFscList([]);
    } finally {
      setLoadingFsc(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCenters();
    fetchFscList();
  }, [fetchCenters, fetchFscList]);

  const handleModelChange = (event) => {
    const modelId = event.target.value;
    setIsSearched(false);
    setSelectedModel(modelId);
    setSelectedCenter("");
    setFacilitatorList([]);
  };

  const filteredCenters = useMemo(() => {
    if (!selectedModel) return [];
    return centerList.filter(
      (center) => String(center.newModelId) === String(selectedModel)
    );
  }, [centerList, selectedModel]);

  const handleSelect = async () => {
    setIsSearched(true);
    if (!selectedCenter || !token) return;

    try {
      setLoadingFacilitators(true);
      const result = await getFacilitatorsByCenter(token, selectedCenter);
      const data = result?.data || [];
      setFacilitatorList(data);
    } catch (error) {
      console.error("Error fetching facilitators:", error);
      setFacilitatorList([]);
    } finally {
      setLoadingFacilitators(false);
    }
  };

  // --- DATA PREPARATION FOR TABLE & EXCEL DOWNLOAD ---
  const formattedFacilitators = useMemo(() => {
    return facilitatorList.map((item) => ({
      ...item,
      // FSC assign kakapothe straight ga "Not Assigned" ani data lo set chestunnam.
      // Eela cheyadam valla Excel / CSV Download lo direct "Not Assigned" vasthundhi.
      displayFscEmail: item.fscEmail && item.fscEmail.trim() !== "" ? item.fscEmail : "Not Assigned",
    }));
  }, [facilitatorList]);

  const handleRemoveFsc = async (facilitator) => {
    if (!facilitator?.menteeMapId) {
      alert("No active mapping found to remove.");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to remove FSC mapping for ${
          facilitator.emailAddress || "this facilitator"
        }?`
      )
    ) {
      return;
    }

    try {
      setLoadingFacilitators(true);
      const userId = user?.id ? Number(user.id) : null;
      await deactivateFscFacilitatorMapping(
        token,
        facilitator.menteeMapId,
        userId
      );
      alert("FSC mapping removed successfully.");
      await handleSelect();
    } catch (error) {
      alert("Unable to remove FSC mapping.");
    } finally {
      setLoadingFacilitators(false);
    }
  };

  const handleOpenFscDialog = (facilitator) => {
    setSelectedFacilitator(facilitator);
    const currentFsc = facilitator.mentorId ? String(facilitator.mentorId) : "";
    setSelectedFsc(currentFsc);
    setOriginalFsc(currentFsc);
    setSelectedProgram(
      facilitator.programId ? String(facilitator.programId) : ""
    );
    setFscDialogOpen(true);
  };

  const handleCloseFscDialog = () => {
    if (saving) return;
    setFscDialogOpen(false);
    setSelectedFacilitator(null);
    setSelectedFsc("");
    setSelectedProgram("");
    setOriginalFsc("");
  };

  const handleSaveMapping = async () => {
    if (!selectedFacilitator) return;
    if (!selectedFsc) {
      alert("Please select an FSC.");
      return;
    }
    if (!selectedProgram) {
      alert("Please select a Program.");
      return;
    }

    try {
      setSaving(true);
      const userId = user?.id ? Number(user.id) : null;

      if (selectedFacilitator.menteeMapId) {
        await deactivateFscFacilitatorMapping(
          token,
          selectedFacilitator.menteeMapId,
          userId
        );
      }

      const mappingData = {
        mentorId: Number(selectedFsc),
        menteeId: Number(selectedFacilitator.id),
        isActive: "Y",
        programId: Number(selectedProgram),
        createdBy: userId,
      };

      await createFscFacilitatorMapping(token, mappingData);
      alert(
        selectedFacilitator.menteeMapId
          ? "FSC Updated Successfully."
          : "FSC Mapped Successfully."
      );

      handleCloseFscDialog();
      await handleSelect();
    } catch (error) {
      alert("Unable to save FSC mapping.");
    } finally {
      setSaving(false);
    }
  };

  const getAvatarLetter = (name) => {
    if (!name) return "F";
    return name.trim().charAt(0).toUpperCase();
  };

  // --- Table Columns Definition ---
  const columns = [
    {
      name: "userName",
      label: "Facilitator Name",
      options: {
        sort: true,
        filter: false,
        customBodyRender: (value) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "#008397",
                color: "#ffffff",
                fontSize: "0.85rem",
                fontWeight: 700,
              }}
            >
              {getAvatarLetter(value)}
            </Avatar>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
              {value || "-"}
            </Typography>
          </Stack>
        ),
      },
    },
    {
      name: "emailAddress",
      label: "Facilitator Email",
      options: {
        sort: true,
        filter: false,
        customBodyRender: (value) => (
          <Typography variant="body2" sx={{ color: "#475569" }}>
            {value || "-"}
          </Typography>
        ),
      },
    },
    {
      name: "primaryDomain",
      label: "Primary Domain",
      options: {
        sort: true,
        filter: false,
        customBodyRender: (value) => (
          <Typography variant="body2" sx={{ color: "#475569" }}>
            {value || "-"}
          </Typography>
        ),
      },
    },
    {
      name: "specialization",
      label: "Specialization",
      options: {
        sort: true,
        filter: true,
        customBodyRender: (value) => (
          <Typography variant="body2" sx={{ color: "#475569" }}>
            {value || "-"}
          </Typography>
        ),
      },
    },
    {
      name: "displayFscEmail",
      label: "FSC Assigned",
      options: {
        sort: true,
        filter: true,
        filterType: "dropdown",
        filterOptions: {
          names: ["Assigned", "Not Assigned"],
          logic: (val, filterValue) => {
            const isAssigned = val !== "Not Assigned";
            if (filterValue.indexOf("Assigned") >= 0 && isAssigned) return false;
            if (filterValue.indexOf("Not Assigned") >= 0 && !isAssigned) return false;
            return true;
          },
        },
        customBodyRender: (value) => {
          const isAssigned = value !== "Not Assigned";
          return isAssigned ? (
            <Stack spacing={0.5} alignItems="flex-start">
              <Chip
                label="Assigned"
                size="small"
                sx={{
                  bgcolor: "#e6f4ea",
                  color: "#137333",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  height: 20,
                  borderRadius: "4px",
                }}
              />
              <Tooltip title={value} arrow>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#334155",
                    fontWeight: 600,
                    maxWidth: 180,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  {value}
                </Typography>
              </Tooltip>
            </Stack>
          ) : (
            <Chip
              label="Not Assigned"
              size="small"
              sx={{
                bgcolor: "#f1f5f9",
                color: "#64748b",
                fontWeight: 600,
                fontSize: "0.7rem",
                height: 20,
                borderRadius: "4px",
              }}
            />
          );
        },
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        sort: false,
        filter: false,
        download: false, // REMOVE ACTIONS COLUMN FROM EXCEL/CSV
        customBodyRender: (value) => {
          const facilitator = facilitatorList.find(
            (item) => String(item.id) === String(value)
          );
          if (!facilitator) return null;
          const isAssigned = Boolean(facilitator.fscEmail);

          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant={isAssigned ? "outlined" : "contained"}
                startIcon={isAssigned ? <SwapHorizIcon /> : <PersonAddAlt1Icon />}
                onClick={() => handleOpenFscDialog(facilitator)}
                sx={{
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 1.5,
                  height: 32,
                  fontSize: "0.78rem",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  ...(isAssigned
                    ? {
                        borderColor: "#008397",
                        color: "#008397",
                        "&:hover": { bgcolor: "#f0fdfa", borderColor: "#006d7e" },
                      }
                    : {
                        bgcolor: "#008397",
                        "&:hover": { bgcolor: "#006d7e", boxShadow: "none" },
                      }),
                }}
              >
                {isAssigned ? "Change FSC" : "Add FSC"}
              </Button>

              {isAssigned && (
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => handleRemoveFsc(facilitator)}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 1.5,
                    height: 32,
                    fontSize: "0.78rem",
                    whiteSpace: "nowrap",
                    borderColor: "#fca5a5",
                    color: "#dc2626",
                    "&:hover": {
                      bgcolor: "#fef2f2",
                      borderColor: "#ef4444",
                    },
                  }}
                >
                  Remove
                </Button>
              )}
            </Stack>
          );
        },
      },
    },
  ];

  const tableOptions = {
    selectableRows: "none",
    responsive: "standard",
    elevation: 0,
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 20, 30, 50],
    print: false,
    download: true,
    downloadOptions: {
      filename: "Facilitator_FSC_Mapping.csv",
      separator: ",",
    },
    viewColumns: true,
    filter: true,
    search: true,
    pagination: true,
    sort: true,
    searchPlaceholder: "Search facilitators...",
  };

  return (
    <Body>
      <Box >
        {/* HEADER SECTION */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" color="primary">
            Facilitator Management
          </Typography>
        </Box>

        {/* FILTER BAR CARD */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            bgcolor: "#ffffff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
              <InputLabel id="model-select-label">Model</InputLabel>
              <Select
                labelId="model-select-label"
                value={selectedModel}
                label="Model"
                onChange={handleModelChange}
              >
                <MenuItem value="">
                  <em>Select Model</em>
                </MenuItem>
                {modelList.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              disabled={!selectedModel || loadingCenters}
              sx={{ minWidth: 280, flex: 1.5 }}
            >
              <InputLabel id="center-select-label">Center</InputLabel>
              <Select
                labelId="center-select-label"
                value={selectedCenter}
                label="Center"
                onChange={(event) => {
                  setSelectedCenter(event.target.value);
                  setFacilitatorList([]);
                  setIsSearched(false);
                }}
              >
                <MenuItem value="">
                  <em>Select Center</em>
                </MenuItem>
                {filteredCenters.map((center) => (
                  <MenuItem key={center.id} value={center.id}>
                    {center.centerName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={handleSelect}
              disabled={!selectedCenter || loadingFacilitators}
              // startIcon={!loadingFacilitators && <FilterAltIcon />}
              sx={{
                bgcolor: "#008397",
                height: 40,
                px: 3.5,
                borderRadius: "8px",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "#006d7e", boxShadow: "none" },
              }}
            >
              {loadingFacilitators ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Select"
              )}
            </Button>
          </Stack>
        </Paper>

        {/* DATA TABLE CONTAINER */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            bgcolor: "#ffffff",
            "& .MuiTableCell-head": {
              bgcolor: "#008397",
              color: "#ffffff !important",
              // fontWeight: 700,
              // fontSize: "0.85rem",
              py: 1.5,
            },
            "& .MuiTableSortLabel-root, & .MuiTableSortLabel-icon": {
              color: "#ffffff !important",
            },
            "& .MuiTableCell-body": {
              py: 1.5,
            },
          }}
        >
          {loadingFacilitators ? (
            <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress sx={{ color: "#008397" }} />
            </Box>
          ) : !isSearched ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <GroupsIcon sx={{ fontSize: 52, color: "#cbd5e1", mb: 1 }} />
              <Typography variant="body1" sx={{ color: "#475569"}}>
                Please select Model and Center to view facilitators list
              </Typography>
            </Box>
          ) : formattedFacilitators.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <Typography variant="h6" sx={{ color: "#334155" }}>
                No Facilitators Found
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                There are no active facilitators available for the selected center.
              </Typography>
            </Box>
          ) : (
            <MUIDataTable
              title={null}
              data={formattedFacilitators}
              columns={columns}
              options={tableOptions}
            />
          )}
        </Paper>
      </Box>

      {/* DIALOG MODAL */}
      <Dialog
        open={fscDialogOpen}
        onClose={handleCloseFscDialog}
        fullWidth
        
        PaperProps={{
          sx: {
            borderRadius: "14px",
            boxShadow:
              "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ p: 2.5, pb: 1.5, bgcolor: "#ffffff" }}>
          <Box
            sx={{
              display: "flex",
              justify: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ pr: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#008397",
                  fontSize: "1.05rem",
                  lineHeight: 1.2,
                }}
              >
                {selectedFacilitator?.fscEmail
                  ? "Change FSC Mapping"
                  : "Assign FSC Mapping"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontSize: "0.78rem",
                  mt: 0.5,
                  display: "block",
                }}
              >
                Map an FSC and Program to this facilitator
              </Typography>
            </Box>

            <IconButton
              onClick={handleCloseFscDialog}
              disabled={saving}
              size="small"
              sx={{
                color: "#94a3b8",
                bgcolor: "#f8fafc",
                "&:hover": { bgcolor: "#f1f5f9", color: "#475569" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider sx={{ borderColor: "#f1f5f9" }} />

        <DialogContent sx={{ p: 2.5 }}>
          <Box
            sx={{
              p: 1.8,
              mb: 2.5,
              borderRadius: "8px",
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#008397",
                color: "#ffffff",
                width: 38,
                height: 38,
                fontWeight: 700,
                fontSize: "0.9rem",
              }}
            >
              {getAvatarLetter(selectedFacilitator?.userName)}
            </Avatar>
            <Box sx={{ overflow: "hidden" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "#0f172a",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedFacilitator?.userName || "Facilitator"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  display: "block",
                  fontSize: "0.76rem",
                  mt: 0.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedFacilitator?.emailAddress || "No Email Provided"}
              </Typography>
            </Box>
          </Box>

          <Stack spacing={2.5}>
            <Autocomplete
              fullWidth
              size="small"
              options={fscList.filter(
                (fsc) => String(fsc.userId) !== String(selectedFacilitator?.id)
              )}
              getOptionLabel={(fsc) => fsc.fscEmail || ""}
              value={
                fscList.find(
                  (fsc) => String(fsc.userId) === String(selectedFsc)
                ) || null
              }
              onChange={(event, newValue) => {
                setSelectedFsc(newValue ? String(newValue.userId) : "");
              }}
              disabled={loadingFsc}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select FSC"
                  placeholder="Search FSC Email..."
                  size="small"
                />
              )}
            />

            <FormControl fullWidth size="small" disabled={!selectedFsc}>
              <InputLabel id="dialog-program-label">Select Program</InputLabel>
              <Select
                labelId="dialog-program-label"
                value={selectedProgram}
                label="Select Program"
                onChange={(event) => setSelectedProgram(event.target.value)}
              >
                <MenuItem value="">
                  <em>Select Program</em>
                </MenuItem>
                <MenuItem value="6">Odisha ITI</MenuItem>
                <MenuItem value="13">Facilitator Support System</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <Divider sx={{ borderColor: "#f1f5f9" }} />

        <DialogActions sx={{ p: 2, px: 2.5, gap: 1 }}>
          <Button
            onClick={handleCloseFscDialog}
            disabled={saving}
            variant="outlined"
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              color: "#475569",
              borderColor: "#cbd5e1",
              px: 2.2,
              height: 36,
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveMapping}
            disabled={
              saving ||
              !selectedFsc ||
              !selectedProgram ||
              isFormUnchanged
            }
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#008397",
              px: 2.8,
              height: 36,
              boxShadow: "none",
              "&:hover": { bgcolor: "#006d7e", boxShadow: "none" },
              "&.Mui-disabled": {
                bgcolor: "#e2e8f0",
                color: "#94a3b8",
              },
            }}
          >
            {saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Save Mapping"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Body>
  );
};

export default MentorMapping;